/* eslint-disable ember/no-computed-properties-in-native-classes, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { sort, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import PageType from 'linced-app/enums/page-type';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import { templatedPostTinyMCEConfig } from '@linc-technologies/linced-common/utils/tinymce';
import { processTemplate } from '@linc-technologies/linced-common/utils/post-templates';
import {
  byKeys,
  compact,
  mapBy,
} from '@linc-technologies/linced-common/utils/arrays';
import { loadPeople } from '@linc-technologies/linced-common/utils/load-people';
import { restartableTask } from 'ember-concurrency';

export default class PostTemplatesController extends Controller {
  @service
  router;

  @action
  setPageType(value) {
    this.pageType = value;
  }

  queryParams = [
    {
      pageType: 'type',
    },
  ];

  @service
  session;

  @service
  store;

  @service
  students;

  @service
  toaster;

  PageType = PageType;
  @tracked
  pageType = PageType.STUDENT;
  @tracked
  postTemplate = null;
  @tracked
  postTemplateDialogOpen = false;
  @tracked
  saveInProgress = false;
  @tracked editableRoot = false;

  get previewEditorOptions() {
    return templatedPostTinyMCEConfig(this.editableRoot);
  }

  @tracked previewPerson = null;
  @tracked previewCaregiver = null;
  @tracked templateContent = '';

  @computed(
    'previewDialogOpen',
    'templateContent',
    'session.school',
    'previewPerson',
    'previewCaregiver',
  )
  get previewContent() {
    if (!this.previewDialogOpen) {
      // Don't bother generating preview content if we're not previewing.
      return '';
    }
    return processTemplate(this.templateContent, {
      preview: true,
      school: this.session.school,
      person: this.previewPerson,
      caregiver: this.previewCaregiver,
    });
  }

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction')
  get useSecondaryContacts() {
    return this.jurisdiction === Jurisdiction.AU_NSW;
  }

  @computed('model.{staff.[],staffUsers.@each.disabled}')
  get staff() {
    let userIds = mapBy(
      this.model.staffUsers.filter((item) => !item.disabled),
      'id',
    );
    return this.model.staff
      .filter((person) => {
        return userIds.includes(person.userId);
      })
      .sort(byKeys('firstName', 'lastName'));
  }

  @tracked
  caregivers = null;
  sortOrder = Object.freeze(['sequence', 'name']);

  @sort('model.postTemplates', 'sortOrder')
  sortedPostTemplates;

  sortDialogOpen = false;

  @tracked
  description;

  @tracked
  name;
  @tracked previewDialogOpen;

  @action
  edit(postTemplate) {
    postTemplate.reload().then((updated) => {
      this.postTemplateDialogOpen = true;
      this.postTemplate = updated;
    });
  }

  @action
  delete() {}

  @action
  addPostTemplate() {
    this.resetForm();
    this.postTemplateDialogOpen = true;
  }

  @action
  savePostTemplate(props) {
    this.saveInProgress = true;

    let record;
    if (isEmpty(this.postTemplate)) {
      record = this.store.createRecord('posts/post-template', {
        schoolId: this.session.school,
      });
    } else {
      record = this.postTemplate;
    }

    record.setProperties(props);

    record
      .save()
      .then(() => {
        this.router.refresh('admin.post-templates');
        this.postTemplateDialogOpen = false;
        this.toaster.success('Post Template saved successfully.');
      })
      .catch(() => {
        this.toaster.error('Error saving Post Template!');
      })
      .finally(() => {
        this.saveInProgress = false;
      });
  }

  @action
  preview(templateContent, editableRoot) {
    this.templateContent = templateContent;
    this.previewDialogOpen = true;
    this.previewPerson = null;
    this.previewCaregiver = null;
    this.editableRoot = editableRoot;
  }

  // loadCaregivers loads the caregivers of the preview learner, then sets
  // them as the caregivers to select from.
  loadCaregivers = restartableTask(async (person) => {
    // Get all primary contacts for student.
    let contacts = [...person.get('learner.primaryContacts')];
    if (this.useSecondaryContacts) {
      // Add secondary contacts, if applicable.
      contacts.push(...person.get('learner.secondaryContacts'));
    }
    await loadPeople(this.store, mapBy(contacts, 'caregiverId'));
    this.caregivers = compact(mapBy(contacts, 'caregiver'));
  });

  @action
  setPreviewPerson(person) {
    if (person.type === PersonType.LEARNER) {
      this.loadCaregivers.perform(person);
    }
    this.previewPerson = person;
  }

  @action
  removePreviewPerson() {
    this.loadCaregivers.cancelAll();
    this.previewPerson = null;
    this.previewCaregiver = null;
    this.caregivers = null;
  }

  @action
  setPreviewCaregiver(caregiver) {
    this.previewCaregiver = caregiver;
  }

  @action
  removePreviewCaregiver() {
    this.previewCaregiver = null;
  }

  resetForm() {
    this.name = '';
    this.description = '';
    this.postTemplate = '';
    this.editableRoot = false;
  }
}
